import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Divider, InputAdornment } from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';

import AuthContext from '../../context';
import { get, del } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    highlightedNumber: {
        color: '#0d6efd'
    },
    controlsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    deleteBtn: {
        width: '100%',
        backgroundColor: '#dc3545',
        borderColor: '#dc3545',
        '&:hover': {
            backgroundColor: '#dc3545',
            borderColor: '#dc3545',
            boxShadow: 'none'
        }
    }
}));

const TicketSaleDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const sede = JSON.parse(localStorage.getItem('@sede'));
    const { id } = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [state, setState] = useState({
        loading: true,
        ticketSale: {
            idticket_sale: 0,
            code: '',
            date: '',
            product_qty: 0,
            qty: 0,
            total_qty: 0,
            price: 0,
            total: 0,
            notes: '',
            supplier: {},
            location: {},
            product_type_ref: {}
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Ticket sale - detail';

        makeRequest();
    }, []);

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/ticketSale/${id}`);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                ticketSale: response.ticketSale
            }));
        } else {
            console.log('[TicketSaleDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const validate = () => {
        const { idticket_sale } = state.ticketSale;
        if (!Number(idticket_sale)) {
            showToast('Please try again later', 'error');
            return false;
        }
        const cond = window.confirm('Are you sure you want to delete this ticket sale?');
        if (!cond) return false;

        destroy();
    }

    const destroy = async () => {
        showLoader(true);
        const response = await del(`/ticketSale/${state.ticketSale.idticket_sale}`);
        if (response.status === 200) {
            history.replace('/ticket/ticketSale/index');
            showToast(response.message);
        } else {
            console.log('[TicketSaleDetail] destroy error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr || !state.ticketSale?.idticket_sale) return <NotFound />

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Code'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Code'
                                value={state.ticketSale.code}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Date'
                                value={dayjs(state.ticketSale.date).format('MM/DD/YYYY')}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Supplier'
                                value={state.ticketSale.supplier?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Location'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Location'
                                value={state.ticketSale.location?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Sede'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Sede'
                                value={sede.name}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                {
                    state.permissions.delete_attr &&
                    <Grid item xs={6} lg={2} className={classes.controlsContainer}>
                        <Button
                            variant='contained'
                            className={classes.deleteBtn}
                            onClick={validate}
                        >
                            {'Delete'}
                        </Button>

                    </Grid>
                }
            </Grid>

            <Divider className={classes.divider} />

            <Grid container>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Product'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Product'
                                value={state.ticketSale.product_type_ref?.product?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Type'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Type'
                                value={state.ticketSale.product_type_ref?.type?.name ?? ''}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Product qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Product qty'
                                value={state.ticketSale.product_qty}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Qty'
                                value={state.ticketSale.qty}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={2}>
                    <FormGroup>
                        <FormLabel>{'Total qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Total qty'
                                value={state.ticketSale.total_qty}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Price'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Price'
                                value={state.ticketSale.price}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Total'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                inputProps={{ className: classes.highlightedNumber }}
                                startAdornment={<InputAdornment position='start'>{'$'}</InputAdornment>}
                                placeholder='Total'
                                value={state.ticketSale.total}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <FormGroup>
                        <FormLabel>{'Notes'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                placeholder='Notes'
                                value={state.ticketSale.notes}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

        </div>
    );
}

export default TicketSaleDetail;